import React, { useState, useEffect, useCallback } from 'react';
import { Tooltip, Popover, Table, Tag } from 'antd';
import { InboxOutlined, PlusCircleOutlined, ArrowDownOutlined, AppstoreOutlined } from '@ant-design/icons';
import api from '../../services/api';

/**
 * HeaderTodayTotals - totais do dia corrente exibidos no header do painel admin.
 * Mostra 3 chips compactos (Processado / RC adicionado / Descarga em litros) e um
 * Popover "Por máquina" com a quebra por máquina.
 *
 * Eficiência: 1 endpoint agregado no backend (GET /api/dashboard/today-totals),
 * polling de 60s + refetch quando a aba volta ao foco (visibilitychange).
 */
const POLL_INTERVAL = 60_000;

const fmt = (v) => Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 });

const HeaderTodayTotals = ({ compact }) => {
  const [data, setData] = useState({ totals: { processed: 0, rcAdded: 0, drained: 0 }, byMachine: [] });
  const [loading, setLoading] = useState(false);

  const fetchTotals = useCallback(async () => {
    try {
      const res = await api.dashboard.getTodayTotals();
      setData(res.data || { totals: { processed: 0, rcAdded: 0, drained: 0 }, byMachine: [] });
    } catch (err) {
      // Silencioso no header: não incomoda o usuário com toast a cada minuto.
      // eslint-disable-next-line no-console
      console.error('Erro ao carregar totais do dia:', err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTotals();
    const interval = setInterval(fetchTotals, POLL_INTERVAL);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchTotals();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchTotals]);

  const { totals, byMachine } = data;

  const chipStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '1px 6px',
    borderRadius: 12,
    background: '#f5f7fa',
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    color: '#1b3663'
  };

  const machineColumns = [
    { title: 'Máquina', dataIndex: 'code', key: 'code' },
    { title: 'Processado', dataIndex: 'processed', key: 'processed', align: 'right', render: (v) => `${fmt(v)} L` },
    { title: 'RC adicionado', dataIndex: 'rcAdded', key: 'rcAdded', align: 'right', render: (v) => `${fmt(v)} L` },
    { title: 'Descarga', dataIndex: 'drained', key: 'drained', align: 'right', render: (v) => `${fmt(v)} L` }
  ];

  const machineData = byMachine.map((r, i) => ({ ...r, key: r.machine_id ?? `none-${i}` }));

  const popoverContent = (
    <div style={{ minWidth: 360 }}>
      <Table
        size="small"
        pagination={false}
        columns={machineColumns}
        dataSource={machineData}
        locale={{ emptyText: 'Sem atividades hoje' }}
        summary={(rows) => {
          const t = rows.reduce((acc, r) => ({
            processed: acc.processed + (r.processed || 0),
            rcAdded: acc.rcAdded + (r.rcAdded || 0),
            drained: acc.drained + (r.drained || 0)
          }), { processed: 0, rcAdded: 0, drained: 0 });
          return (
            <Table.Summary.Row style={{ fontWeight: 700, background: '#fafafa' }}>
              <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">{fmt(t.processed)} L</Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right">{fmt(t.rcAdded)} L</Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="right">{fmt(t.drained)} L</Table.Summary.Cell>
            </Table.Summary.Row>
          );
        }}
      />
    </div>
  );

  return (
    <div
      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
      role="status"
      aria-label={`Totais de hoje: processado ${fmt(totals.processed)} litros, RC adicionado ${fmt(totals.rcAdded)} litros, descarga ${fmt(totals.drained)} litros`}
    >
      <Tooltip title="Processado hoje (litros)">
        <Tag style={chipStyle} color="blue">
          <InboxOutlined />
          <span>{compact ? 'Proc:' : 'Processado:'}</span>
          <span>{fmt(totals.processed)} L</span>
        </Tag>
      </Tooltip>

      <Tooltip title="RC adicionado às máquinas hoje (litros)">
        <Tag style={chipStyle} color="orange">
          <PlusCircleOutlined />
          <span>{compact ? 'RC:' : 'RC:'}</span>
          <span>{fmt(totals.rcAdded)} L</span>
        </Tag>
      </Tooltip>

      <Tooltip title="Descarga hoje (litros)">
        <Tag style={chipStyle} color="green">
          <ArrowDownOutlined />
          <span>{compact ? 'Desc:' : 'Descarga:'}</span>
          <span>{fmt(totals.drained)} L</span>
        </Tag>
      </Tooltip>

      <Popover content={popoverContent} title="Por máquina (hoje)" trigger="click" placement="bottomRight">
        <Tag style={{ ...chipStyle, cursor: 'pointer' }} color="default">
          <AppstoreOutlined />
          <span>Por máquina</span>
        </Tag>
      </Popover>
    </div>
  );
};

export default HeaderTodayTotals;
